import { Typography } from 'antd';
import React, { useState } from 'react';
import styles from './index.module.css';

type Props = {
  schemeData: any;
};

const MutualFundCard: React.FC<Props> = ({ schemeData }) => {
  const [isAccountsExpanded, setIsAccountsExpanded] = useState(false);

  // Extract scheme name from amcName as per requirement
  const schemeName = schemeData?.amcName || 'Unknown Scheme';

  // Extract folio numbers from accountDetails
  const folioNumbers = schemeData?.folioNumbers || [];

  // Use scheme-level values directly
  const totalUnits = parseFloat(schemeData?.units || 0);
  const totalCurrentValue = parseFloat(schemeData?.currentValue || 0);
  const nav = schemeData?.nav || 0;

  // Generate logo URL from amcName
  const getLogoUrl = (amcName: string) => {
    if (!amcName) return '';
    // Convert to Title Case (first letter capital, rest lowercase) and replace spaces with underscores
    const formattedName = amcName
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('_');
    return `https://logo.ignosis.ai/fund/${formattedName}.png`;
  };

  const logoUrl = getLogoUrl(schemeName);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const dayjs = require('dayjs');
    return dayjs(dateStr).format("DD MMM 'YY");
  };

  const formatNumber = (num: number | string) => {
    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number)) return '0';
    return number.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  const renderFolioAccounts = () => {
    // Filter out any null/undefined values
    const validFolioNumbers = folioNumbers.filter((folio: any) => folio);

    if (validFolioNumbers.length === 0) {
      return null;
    }

    // For 1-2 folios, show them directly
    if (validFolioNumbers.length <= 2) {
      return (
        <>
          {validFolioNumbers.map((folio: string, index: number) => (
            <div key={index} className={styles.folioTag}>
              ** {folio?.slice(-4) || '****'}
            </div>
          ))}
        </>
      );
    }

    // For more than 2 folios, show expandable view
    if (isAccountsExpanded) {
      return (
        <>
          {validFolioNumbers.map((folio: string, index: number) => (
            <div key={index} className={styles.folioTag}>
              ** {folio?.slice(-4) || '****'}
            </div>
          ))}
          <div
            className={styles.folioTagMore}
            onClick={(e) => {
              e.stopPropagation();
              setIsAccountsExpanded(false);
            }}
            style={{ cursor: 'pointer' }}
          >
            Show less
          </div>
        </>
      );
    }

    return (
      <>
        <div className={styles.folioTag}>
          ** {validFolioNumbers[0]?.slice(-4) || '****'}
        </div>
        <div
          className={styles.folioTagMore}
          onClick={(e) => {
            e.stopPropagation();
            setIsAccountsExpanded(true);
          }}
          style={{ cursor: 'pointer' }}
        >
          +{validFolioNumbers.length - 1} accounts
        </div>
      </>
    );
  };

  return (
    <div className={styles.mfCard}>
      <div className={styles.mfCardTop}>
        <div className={styles.mfCardHeader}>
          <div className={styles.schemeIcon}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={schemeName}
                className={styles.schemeLogoImage}
                onError={(e) => {
                  // Fallback to SVG if image fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove(
                    styles.hidden
                  );
                }}
              />
            ) : null}
            <svg
              width='32'
              height='32'
              viewBox='0 0 32 32'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className={logoUrl ? styles.hidden : ''}
            >
              <rect width='32' height='32' rx='8' fill='#F6F6F8' />
              <path d='M16 9L19 15H13L16 9Z' fill='#002EDC' />
              <rect x='13' y='17' width='6' height='6' fill='#002EDC' />
            </svg>
          </div>
          <div className={styles.schemeInfo}>
            <div className={styles.schemeHeader}>
              <Typography.Text className={styles.schemeName}>
                {schemeName}
              </Typography.Text>
            </div>
            <div className={styles.folioTags}>{renderFolioAccounts()}</div>
          </div>
        </div>
      </div>

      <div className={styles.divider}></div>

      <div className={styles.mfCardDetails}>
        <div className={styles.detailItem}>
          <Typography.Text className={styles.detailLabel}>
            Units
          </Typography.Text>
          <Typography.Text className={styles.detailValue}>
            {formatNumber(totalUnits)}
          </Typography.Text>
        </div>

        <div className={styles.detailDivider}></div>

        <div className={styles.detailItem}>
          <Typography.Text className={styles.detailLabel}>NAV</Typography.Text>
          <Typography.Text className={styles.detailValue}>
            {formatNumber(nav)}
          </Typography.Text>
        </div>

        <div className={styles.detailDivider}></div>

        <div className={styles.detailItem}>
          <Typography.Text className={styles.detailLabel}>
            Current Value
          </Typography.Text>
          <Typography.Text className={styles.detailValue}>
            {formatNumber(totalCurrentValue)}
          </Typography.Text>
        </div>
      </div>
    </div>
  );
};

export default MutualFundCard;
